import styled, { css } from "styled-components";
import { BREAKPOINTS } from "../../variables/Breakpoints";
import { COLOURS, BORDERS } from "../../variables/Colors";
import { setFlex, setPosition } from "../../variables/Position";

const randomiseJustify = id => {
  const modes = ["center", "flex-start", "flex-end"];
  return modes[id % modes.length];
};

const setFolderScrollTranslate = position => {
  switch (position) {
    case "left":
      return css`
        transform: translateX(0%);
        @media (max-width: ${BREAKPOINTS.sm}px) {
          transform: translateY(100%);
        }
      `;
    case "right":
      return css`
        transform: translateX(0%);
        @media (max-width: ${BREAKPOINTS.sm}px) {
          transform: translateY(-100%);
        }
      `;
    default:
      return;
  }
};

const setFolderOrigin = position => {
  switch (position) {
    case "left":
      return css`
        transform-origin: left center;
        @media (max-width: ${BREAKPOINTS.sm}px) {
          transform-origin: bottom center;
        }
      `;
    case "center":
      return css`
        transform-origin: center center;
      `;
    case "right":
      return css`
        transform-origin: right center;
        @media (max-width: ${BREAKPOINTS.sm}px) {
          transform-origin: top center;
        }
      `;
    default:
      return;
  }
};

const setFolderTransform = position => {
  switch (position) {
    case "left":
      return `
      ${setPosition({ position: "absolute", top: "0", left: "0" })};
      transform: translateZ(0px) translate3d(0, 0, 0) rotateY(120deg) scale3d(1.002, 1, 1);
      @media (max-width: ${BREAKPOINTS.sm}px) {
        transform: translateZ(-1px) translate3d(0, -80vh, 0) rotateX(-120deg) scale3d(1, 1.002, 1);
      }
      -webkit-backface-visibility: hidden;
      `;
    case "center":
      return `
      ${setFlex({ justify: "center" })};
      overflow: hidden;
      `;
    case "right":
      return `
      ${setPosition({
        position: "absolute",
        top: "0",
        right: "0",
      })};
      transform: translateZ(0px) translate3d(0, 0, 0) rotateY(-120deg) scale3d(1.002, 1, 1);
      @media (max-width: ${BREAKPOINTS.sm}px) {
        transform: translateZ(-1px) translate3d(0, 80vh, 0) rotateX(120deg) scale3d(1, 1.002, 1);
      }
      -webkit-backface-visibility: hidden;
      `;
    default:
      return;
  }
};

export const Headline = styled.h3`
  font-family: gantserif-medium-condensed;
  font-weight: 500;
  color: ${COLOURS.primary.black};
  ${props =>
    props.$isActive &&
    `
  color: ${COLOURS.primary.white};
    @media (max-width: ${BREAKPOINTS.sm}px) {
      color: ${COLOURS.primary.black};
    }
  `}
  margin: 0;
  text-align: left;
  font-size: 1.1rem;
  line-height: 1.2rem;
  &.headline,
  .headline {
    margin: 1.5rem 0;
    font-size: 2rem;
    line-height: 2rem;
    @media (max-width: ${BREAKPOINTS.sm}px) {
      font-size: 1.8rem;
      line-height: 1.8rem;
    }
  }
  ${props => props.$margin && `margin-top: 1rem;`};
`;

export const Link = styled.a`
  font-family: gantserif-medium-condensed;
  font-weight: 500;
  color: ${COLOURS.primary.black};
  text-decoration: underline;
`;

export const Subheading = styled.h4`
  font-family: gantmodern-regular;
  color: ${COLOURS.primary.black};
  font-weight: 500;
  ${props =>
    props.$isActive &&
    `
  color: ${COLOURS.primary.white};
    @media (max-width: ${BREAKPOINTS.sm}px) {
      color: ${COLOURS.primary.black};
    }
  `}
  margin: 0;
  text-align: left;
  ${props => props.$whiteWrap && "white-space: nowrap;"};
  ${props => props.$padding && `padding-bottom: 2rem;`};
`;

export const Wrapper = styled.div`
  ${props =>
    setPosition({
      position: props.$position || "relative",
      top: "0",
    })};
  overflow: hidden;
  width: 100%;
  &.fixed {
    ${setPosition({ position: "fixed", top: "0" })};
    transform: translateZ(0);
  }
  ${props => props.$background && `background: ${props.$background};`};
  ${props => props.$active && "min-height: 100vh"};
`;

export const CollectionContainer = styled.div`
  ${setFlex({ direction: "column" })};
  width: 100%;
  &.scroll {
    overflow: scroll;
  }
`;

export const Screen = styled.div`
  ${setFlex({ justify: "center", align: "center" })};
  width: 100vw;
  height: 100vh;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    height: 100vh;
  }
`;

export const Container = styled.div`
  ${setPosition({ position: "relative" })};
  ${setFlex({ justify: "center", align: "center" })};
  perspective: 150px;
  transform-style: preserve-3d;
  height: 100%;
  min-height: 100vh;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  width: calc(100vw - 150px);
  @media (max-width: ${BREAKPOINTS.sm}px) {
    min-height: 80vh;
    width: 100%;
    margin: 8rem 0 0 0;
  }
`;

export const CollectionWrapper = styled.div`
  ${setFlex({ justify: "flex-start", align: "center" })};
  ${setPosition({ position: "absolute", top: "0", left: "0" })};
  flex-wrap: nowrap;
  height: fit-content;
  will-change: transform;
  overflow: hidden;
  opacity: 0;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${setFlex({ justify: "center", direction: "column" })};
    width: 100%;
    overflow: visible;
  }
`;

export const Fold = styled.div`
  width: calc(100vw - 150px);
  height: 100vh;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    overflow: hidden;
    height: 80vh;
    width: 100%;
  }
  will-change: transform;
  ${props => setFolderOrigin(props.$position)};
  ${props => setFolderTransform(props.$position)};
  > div {
    -webkit-transform: translate3d(0, 0, 0);
  }
`;

export const FoldSize = styled.div`
  ${setFlex({})};
  width: 100%;
  height: 100%;
  transform: scaleX(0.998);
  @media (max-width: ${BREAKPOINTS.sm}px) {
    transform: scaleY(0.998);
  }
  // will-change: transform;
  ${props => setFolderOrigin(props.$position)};
  ${props =>
    props.$position === "center" &&
    `
${setPosition({ position: "absolute", top: "0", left: "0" })};
`};
`;

export const CenterWrapper = styled.div`
  ${setPosition({ position: "absolute", top: "0", left: "0", zIndex: 1 })};
  ${setFlex({ justify: "center", align: "center" })};
  background-color: ${COLOURS.primary.white};
  @media (max-width: ${BREAKPOINTS.sm}px) {
    width: 100%;
  }
`;

export const CenterImage = styled.img`
  ${setPosition({ position: "absolute" })};
  transform: translateZ(0);
  width: 80vw;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    width: 100vw;
  }
`;

export const FrostBackdrop = styled.div`
  ${setPosition({ position: "relative", zIndex: 2 })};
  width: 100%;
  height: 100%;
  background: inherit;
  overflow: hidden;

  &:before {
    ${setPosition({
      position: "absolute",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      zIndex: -1,
    })};
    content: "";
    border: 1px solid ${BORDERS.secondary};
    will-change: transform;
    background: inherit;
    backdrop-filter: ${props => (props.$archive ? "blur(50px)" : "blur(10px)")};
    backface-visibility: hidden;
    transform: translate3d(0, 0, 0);
    -webkit-backface-visibility: hidden;
    -webkit-transform: translate3d(0, 0, 0);
    -webkit-backdrop-filter: ${props =>
      props.$archive ? "blur(50px)" : "blur(10px)"};
    background: linear-gradient(
      88deg,
      rgba(255, 255, 255, 0.1) 51.69%,
      rgba(255, 255, 255, 0) 150.79%
    );
  }

  @media (max-width: ${BREAKPOINTS.sm}px) {
    width: 80%;
  }
`;

export const FoldScroller = styled.div`
  ${setFlex({})};
  width: 100%;
  height: 100%;
  ${props => setFolderScrollTranslate(props.$position)};
  // will-change: transform;
`;

export const Content = styled.div`
  ${setFlex({})};
  width: 100%;
  height: 100%;
`;

export const PLP = styled.div`
  ${setFlex({})};
  height: 100vh;
  min-width: 75vw;
  margin: 0 10vw;
  overflow: hidden;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${setPosition({ position: "relative" })};
    height: auto;
    width: 100%;
    margin: 5rem 0;
    overflow: visible;
  }
`;

export const ArchiveTile = styled.div`
  height: 100vh;
  width: 65vw;
  margin: 0 10vw;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    display: none;
  }
`;

export const ArchiveContentContainer = styled.div`
  ${setFlex({ direction: "column" })};
  height: 100%;
  padding: 3rem;
`;

export const ArchiveContent = styled.div`
  ${setFlex({ justify: "center", align: "flex-start", direction: "column" })};
  height: 100%;
  width: 100%;
`;

export const PLPContentContainer = styled.div`
  ${setPosition({ position: "relative", left: -1 })};
  min-width: 30vw;
  width: 100%;
  height: 100%;
  cursor: pointer;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${props =>
      setFlex({
        justify: randomiseJustify(props.$justifyId),
        align: "center",
      })};
    ${setPosition({ position: "absolute", bottom: -100 })};
    width: 100%;
    height: 20vh;
  }
`;

export const PLPContent = styled.div`
  ${setPosition({ position: "absolute", top: "0", left: "0" })};
  ${setFlex({ justify: "center", align: "center" })};
  ${props =>
    props.$isActive &&
    `
    @media (min-width: ${BREAKPOINTS.sm}px) {
     mix-blend-mode: difference;
    }
  `};
  width: 100%;
  height: 100%;
`;

export const PLPHeading = styled.div`
  ${setFlex({ justify: "flex-start", align: "center" })};
  width: 100%;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    padding: 0 1.5rem;
  }
`;

export const PLPNumber = styled.div`
  ${setFlex({ justify: "center", align: "center" })};
`;

export const PLPTitle = styled.div`
  ${setFlex({ justify: "center", align: "center" })};
  width: 100%;
`;

export const PrimaryImageWrapper = styled.div`
  ${setPosition({ position: "static" })};
  height: 100%;
  border: 1px solid ${BORDERS.secondary};
  border-right-width: 0;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    min-height: 80vh;
    width: 100%;
  }
  @media (max-width: ${BREAKPOINTS.sm}px) {
    min-height: auto;
    min-width: 100%;
    height: auto;
    border: none;
  }
`;

export const SecondaryWrapper = styled.div`
  ${props =>
    props.$plpSecondary &&
    `background: url(${props.$plpSecondary}) no-repeat center center; background-size: cover;`};
  ${props => props.$background && `background: ${props.$background}`};
  opacity: ${props => (props.$isActive ? 1 : 0)};
  width: 100%;
  height: 100%;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    width: 100%;
    background: none;
  }
`;

export const Image = styled.img`
  width: auto;
  max-width: 100%;
  height: 100%;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    width: 100%;
  }
  ${props => props.$isActive && "mix-blend-mode: difference"};
`;

export const PLPImage = styled.img`
  height: 100%;
  width: auto;
  max-width: unset;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    height: ${props => {
      return `calc(${100 / props.$aspectRatio}vw - ${
        30 / props.$aspectRatio
      }px);`;
    }};
    padding: 0 1rem;
  }
  transition: preserve-3d;
  -webkit-transform-style: preserve-3d;
  ${setPosition({ position: "static" })};
`;
